import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { INVOICE_STATUS } from '../constants/enums';
import AuthContext from '../contexts/AuthContext';
import InvoicesToPayContext from '../contexts/InvoicesToPayContext';
import MerchantContext from '../contexts/MerchantContext';
import { useTRPC } from '../trpc/client';
import { documentTypes } from '../types';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';

export function useDeeplinkLogin() {
  const trpc = useTRPC();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(false);
  const { merchant } = useContext(MerchantContext);
  const { login } = useContext(AuthContext);
  const { addInvoiceToPay } = useContext(InvoicesToPayContext);

  const invoiceId = searchParams.get('factura');
  const urlDocumentType = z
    .enum([...documentTypes, 'ptp'])
    .transform((type) => (type === 'ptp' ? 'ppt' : type))
    .safeParse(searchParams.get('tipo')).data;
  const urlUser = searchParams.get('cliente');

  const queryOptions = trpc.invoices.get.queryOptions({
    rentalsMerchantId: merchant?.id ?? '',
    ids: [invoiceId ?? ''],
  });

  const { data, isFetched, isError } = useQuery({
    ...queryOptions,
    enabled: !!(invoiceId && merchant?.id),
    retry: false,
  });

  useEffect(() => {
    // Only process if we have some deeplink params
    if (!invoiceId && !urlUser) return;

    setIsProcessing(true);

    // If we have invoiceId, wait for it to finish loading
    if (invoiceId && !isFetched && !isError) return;

    // Determine what document info to use:
    // if invoiceId is provided, use the info from the invoice, otherwise use the info from the url params
    const user = (merchant?.loginByContract ? data?.[0]?.contract : data?.[0]?.customerDocumentNumber) || urlUser;
    const documentType = data?.[0]?.customerDocumentType || urlDocumentType;

    // Login if we have a document number
    if (user) login(user, documentType as 'cc' | 'ce' | 'nit' | undefined, data?.[0]?.customerDocumentNumber);

    // Add invoice if we have one
    if (data?.[0]?.status === INVOICE_STATUS.READY && !data?.[0].paymentDate) addInvoiceToPay(data?.[0], false, -2);

    setIsProcessing(false);
  }, [invoiceId, urlUser, urlDocumentType, data, login, addInvoiceToPay, isFetched, isError, merchant?.loginByContract]);

  return { isProcessing };
}
